import type {
  ProjectTaskViewCommand,
  ProjectTaskViewState,
} from '@/modules/projects/task-views.api'
import { projectTaskViewsApi } from '@/modules/projects/task-views.api'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { ViewConfig } from '@scrambl/shared'
import { PreferenceScopeEnum } from '@scrambl/shared'

const EMPTY: ProjectTaskViewState = { userViews: [], sharedViews: [] }

/** Personal commands carry only this actor's edits. Shared commands are
 * authorized and merged with the locked project by the application service. */
export function useProjectTaskViews({
  projectId,
  canManage = false,
  enabled = true,
}: {
  projectId: string
  canManage?: boolean
  enabled?: boolean
}) {
  const { user } = useAuth()
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const queryClient = useQueryClient()
  const queryKey = [
    'project-task-views',
    organizationId,
    user?.id,
    projectId,
  ] as const
  const query = useQuery({
    queryKey,
    queryFn: () => projectTaskViewsApi.get(projectId, organizationId!),
    enabled: enabled && !!projectId && !!organizationId && !!user?.id,
  })
  const state = query.data ?? EMPTY
  const views = [...state.sharedViews, ...state.userViews]
  const activeView = views.find(view => view.id === state.activeViewId)

  const execute = async (command: ProjectTaskViewCommand) => {
    if (!enabled || !projectId || !organizationId || !user?.id)
      throw new Error('Select a workspace before saving a view')
    await projectTaskViewsApi.execute(projectId, organizationId, command)
    // Refetch the authoritative state instead of applying a possibly late
    // command response over a newer concurrent edit.
    await queryClient.invalidateQueries({ queryKey })
  }
  const createView = async (
    name: string,
    config: ViewConfig,
    scope = PreferenceScopeEnum.USER,
    _query?: string | null,
  ) => {
    await execute({
      action: 'save',
      viewId: crypto.randomUUID(),
      name: name.trim() || 'New view',
      config,
      scope:
        scope === PreferenceScopeEnum.ORGANIZATION && canManage
          ? 'organization'
          : 'user',
    })
  }
  const saveActiveView = async (config: ViewConfig, _query?: string | null) => {
    if (
      !activeView ||
      (activeView.scope === PreferenceScopeEnum.ORGANIZATION && !canManage)
    ) {
      await createView(activeView ? `My ${activeView.name}` : 'My view', config)
      return
    }
    await execute({
      action: 'save',
      viewId: activeView.id,
      name: activeView.name,
      config,
      scope:
        activeView.scope === PreferenceScopeEnum.ORGANIZATION
          ? 'organization'
          : 'user',
    })
  }
  const deleteView = async (viewId: string) => {
    const view = views.find(item => item.id === viewId)
    if (!view) return
    if (view.scope === PreferenceScopeEnum.ORGANIZATION && !canManage) return
    await execute({
      action: 'delete',
      viewId,
      scope:
        view.scope === PreferenceScopeEnum.ORGANIZATION
          ? 'organization'
          : 'user',
    })
  }
  return {
    ...state,
    views,
    activeView,
    isLoading: query.isLoading,
    createView,
    saveActiveView,
    deleteView,
    setActiveViewId: (viewId: string) => execute({ action: 'select', viewId }),
    setDefaultViewId: async (viewId: string) => {
      if (canManage) await execute({ action: 'default', viewId })
    },
  }
}
